// CoLiviq-staff Operators management (Neel feedback #3) — staff-only.
//
// Lists the operator organizations on the platform and, per operator, the
// review inbox for their bank-connect onboarding requests (#7): approve to
// switch on open banking (the finAPI hookup lands later — approval is the
// gate), or reject with a note. Staff-scoped by RLS: operators read only their
// own org, staff read/manage all.
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { AdminV2Layout } from "@/components/admin/v2/AdminV2Layout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { edgeFunctionErrorMessage } from "@/lib/edge-function-error";
import { Landmark, Loader2, Trash2, Users } from "lucide-react";

interface OperatorRow {
  id: string;
  name: string;
  slug: string;
  contact_email: string | null;
  created_at: string;
  units_managed: string | null;
  primary_market: string | null;
  phone: string | null;
  signup_note: string | null;
}
interface BankSubmission {
  id: string;
  operator_id: string;
  account_holder: string;
  iban: string;
  bic: string | null;
  status: "pending" | "approved" | "rejected";
  submitted_at: string;
  review_note: string | null;
}

const SUB_TONE: Record<string, string> = {
  active: "bg-emerald-100 text-emerald-700",
  past_due: "bg-amber-100 text-amber-700",
  canceled: "bg-slate-100 text-slate-600",
};

export default function OperatorsAdminPage() {
  const { user, isSuperAdmin } = useAuth();
  const qc = useQueryClient();
  const [openFor, setOpenFor] = useState<OperatorRow | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data: operators = [], isLoading } = useQuery({
    queryKey: ["admin-operators"],
    queryFn: async (): Promise<OperatorRow[]> => {
      const { data, error } = await supabase
        .from("operators")
        .select(
          "id, name, slug, contact_email, created_at, is_platform, units_managed, primary_market, phone, signup_note"
        )
        .eq("is_platform", false)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as OperatorRow[];
    },
  });

  const { data: subs = [] } = useQuery({
    queryKey: ["admin-operators-subs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("saas_subscriptions")
        .select("operator_id, status");
      if (error) throw error;
      return (data ?? []) as { operator_id: string; status: string }[];
    },
  });

  const { data: submissions = [] } = useQuery({
    queryKey: ["admin-operators-bank-submissions"],
    queryFn: async (): Promise<BankSubmission[]> => {
      const { data, error } = await supabase
        .from("operator_bank_submissions")
        .select(
          "id, operator_id, account_holder, iban, bic, status, submitted_at, review_note"
        )
        .order("submitted_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as BankSubmission[];
    },
  });

  // The org's super_admin — the person who filled out the signup form.
  const { data: primaryContact } = useQuery({
    queryKey: ["admin-operator-primary-contact", openFor?.id],
    enabled: !!openFor?.id,
    queryFn: async (): Promise<{ name: string | null } | null> => {
      const { data: mem } = await supabase
        .from("operator_members")
        .select("user_id")
        .eq("operator_id", openFor!.id)
        .eq("member_role", "super_admin")
        .maybeSingle();
      if (!mem?.user_id) return null;
      const { data: prof } = await supabase
        .from("profiles")
        .select("name")
        .eq("id", mem.user_id)
        .maybeSingle();
      return (prof as { name: string | null }) ?? null;
    },
  });

  const subByOp = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of subs) m.set(s.operator_id, s.status);
    return m;
  }, [subs]);
  const pendingByOp = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of submissions)
      if (s.status === "pending")
        m.set(s.operator_id, (m.get(s.operator_id) ?? 0) + 1);
    return m;
  }, [submissions]);

  const review = useMutation({
    mutationFn: async (v: { id: string; status: "approved" | "rejected" }) => {
      const { error } = await supabase
        .from("operator_bank_submissions")
        .update({
          status: v.status,
          reviewed_by: user?.id ?? null,
          reviewed_at: new Date().toISOString(),
        })
        .eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: (_d, v) => {
      toast.success(
        v.status === "approved"
          ? "Approved — open banking can be provisioned."
          : "Rejected."
      );
      qc.invalidateQueries({ queryKey: ["admin-operators-bank-submissions"] });
    },
    onError: (e: unknown) =>
      toast.error(
        e instanceof Error ? e.message : "Could not update the request."
      ),
  });

  // Super-admin only: delete an operator org + its members' auth users (frees
  // the emails). The edge function refuses if the org still owns business data.
  const del = useMutation({
    mutationFn: async (operatorId: string) => {
      const res = await supabase.functions.invoke("operator-admin-delete", {
        body: { operator_id: operatorId },
      });
      if (res.error)
        throw new Error(
          await edgeFunctionErrorMessage(res.error, "Could not delete operator")
        );
      return res.data as { deleted_members?: number };
    },
    onSuccess: (data) => {
      toast.success(
        `Operator deleted — freed ${data?.deleted_members ?? 0} account(s).`
      );
      setOpenFor(null);
      setConfirmDelete(false);
      qc.invalidateQueries({ queryKey: ["admin-operators"] });
      qc.invalidateQueries({ queryKey: ["admin-operators-subs"] });
      qc.invalidateQueries({ queryKey: ["admin-operators-bank-submissions"] });
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Could not delete operator"),
  });

  const detailSubs = openFor
    ? submissions.filter((s) => s.operator_id === openFor.id)
    : [];

  return (
    <AdminV2Layout title="Operators">
      <div className="mx-auto max-w-5xl space-y-6">
        <p className="text-muted-foreground max-w-2xl text-sm sm:text-base">
          Operator organizations on the platform, their subscription, and their
          bank-connect onboarding requests to review.
        </p>

        {isLoading ? (
          <div className="flex min-h-[30vh] items-center justify-center">
            <Loader2
              className="h-6 w-6 animate-spin text-muted-foreground"
              aria-label="Loading operators"
            />
          </div>
        ) : operators.length === 0 ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">No operators yet</CardTitle>
              <CardDescription>
                Operator organizations appear here once they sign up at
                /operators.
              </CardDescription>
            </CardHeader>
          </Card>
        ) : (
          <div className="grid gap-3">
            {operators.map((op) => {
              const status = subByOp.get(op.id);
              const pending = pendingByOp.get(op.id) ?? 0;
              return (
                <Card key={op.id}>
                  <CardContent className="flex flex-wrap items-center gap-4 py-4">
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-foreground">
                        {op.name}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {op.contact_email ?? op.slug}
                      </p>
                    </div>
                    <Badge
                      variant="secondary"
                      className={status ? (SUB_TONE[status] ?? "") : ""}
                    >
                      {status ? `Subscription: ${status}` : "No subscription"}
                    </Badge>
                    {pending > 0 && (
                      <Badge className="bg-amber-100 text-amber-700">
                        <Landmark className="mr-1 h-3.5 w-3.5" aria-hidden />
                        {pending} to review
                      </Badge>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setOpenFor(op)}
                    >
                      Manage
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <Dialog
          open={!!openFor}
          onOpenChange={(o) => {
            if (!o) {
              setOpenFor(null);
              setConfirmDelete(false);
            }
          }}
        >
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{openFor?.name}</DialogTitle>
              <DialogDescription>
                {openFor?.contact_email ?? openFor?.slug} ·{" "}
                {openFor && (subByOp.get(openFor.id) ?? "no subscription")}
              </DialogDescription>
            </DialogHeader>

            {/* Signup details — everything the operator filled out on the
                /operators demo form (Neel #2). */}
            {openFor && (
              <dl className="grid grid-cols-3 gap-x-3 gap-y-2 rounded-lg border border-border/60 p-3 text-sm">
                <dt className="text-muted-foreground">Contact</dt>
                <dd className="col-span-2 text-foreground">
                  {primaryContact?.name || "—"}
                  {openFor.contact_email ? ` · ${openFor.contact_email}` : ""}
                </dd>
                <dt className="text-muted-foreground">Units managed</dt>
                <dd className="col-span-2 text-foreground">
                  {openFor.units_managed || "—"}
                </dd>
                <dt className="text-muted-foreground">Primary market</dt>
                <dd className="col-span-2 text-foreground">
                  {openFor.primary_market || "—"}
                </dd>
                <dt className="text-muted-foreground">Phone</dt>
                <dd className="col-span-2 text-foreground">
                  {openFor.phone || "—"}
                </dd>
                <dt className="text-muted-foreground">Notes</dt>
                <dd className="col-span-2 whitespace-pre-wrap text-foreground">
                  {openFor.signup_note || "—"}
                </dd>
              </dl>
            )}

            <div className="space-y-3">
              <p className="flex items-center gap-2 text-sm font-medium text-foreground">
                <Landmark className="h-4 w-4" aria-hidden />
                Bank-connect requests
              </p>
              {detailSubs.length === 0 ? (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Users className="h-4 w-4" aria-hidden />
                  No requests from this operator yet.
                </p>
              ) : (
                detailSubs.map((s) => (
                  <div
                    key={s.id}
                    className="rounded-lg border border-border/60 p-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium">
                        {s.account_holder}
                      </span>
                      <Badge
                        variant="secondary"
                        className={
                          s.status === "approved"
                            ? SUB_TONE.active
                            : s.status === "rejected"
                              ? SUB_TONE.canceled
                              : "bg-amber-100 text-amber-700"
                        }
                      >
                        {s.status}
                      </Badge>
                    </div>
                    <p className="mt-1 font-mono text-xs text-muted-foreground">
                      {s.iban}
                      {s.bic ? ` · ${s.bic}` : ""}
                    </p>
                    {s.status === "pending" && (
                      <div className="mt-3 flex gap-2">
                        <Button
                          size="sm"
                          disabled={review.isPending}
                          onClick={() =>
                            review.mutate({ id: s.id, status: "approved" })
                          }
                        >
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={review.isPending}
                          onClick={() =>
                            review.mutate({ id: s.id, status: "rejected" })
                          }
                        >
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {isSuperAdmin && openFor && (
              <div className="mt-2 border-t border-border/60 pt-4">
                {!confirmDelete ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setConfirmDelete(true)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" aria-hidden />
                    Delete operator
                  </Button>
                ) : (
                  <div className="space-y-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3">
                    <p className="text-sm text-foreground">
                      Permanently delete <strong>{openFor.name}</strong> and its
                      member account(s)? This frees their email addresses and
                      can't be undone. Refused if the operator still owns
                      properties, tenants or other data.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="destructive"
                        size="sm"
                        disabled={del.isPending}
                        onClick={() => del.mutate(openFor.id)}
                      >
                        {del.isPending ? "Deleting…" : "Delete permanently"}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={del.isPending}
                        onClick={() => setConfirmDelete(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminV2Layout>
  );
}
